<template>
    <Layout>
        <div class="relative bg-white">
            <div class="absolute inset-0">
                <div class="absolute inset-y-0 left-0 w-1/2 bg-gray-50" />
            </div>
            <div class="relative mx-auto max-w-7xl lg:grid lg:grid-cols-5">
                <div class="bg-gray-50 px-4 py-16 sm:px-6 lg:col-span-2 lg:px-8 lg:py-24 xl:pr-12">
                    <div class="mx-auto max-w-lg">
                        <h2 class="text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">
                            {{ $t('Contact us') }}
                        </h2>
                        <p class="mt-3 text-lg leading-6 text-gray-500">
                            {{ $t('You have a question or a problem? Send us a message and we will get back to you as soon as possible.') }}
                        </p>
                        <dl class="mt-8 text-base text-gray-500">
                            <div class="mt-6">
                                <dt class="sr-only">{{ $t('Phone number') }}</dt>
                                <dd class="flex">
                                    <p class="ml-3 flex items-center space-x-2">
                                        <IconPhone :size="20" />
                                        <span>1-450-403-4300</span>
                                    </p>
                                </dd>
                            </div>
                            <div class="mt-3">
                                <dt class="sr-only">{{ $t('Email address') }}</dt>
                                <dd class="flex">
                                    <p class="ml-3 flex items-center space-x-2">
                                        <IconMail :size="20" />
                                        <span>support@camaq.ca</span>
                                    </p>
                                </dd>
                            </div>
                        </dl>

                        <div class="mt-8 flex items-center space-x-4">
                            <a
                                href="https://www.facebook.com/camaq/"
                                target="_blank"
                                rel="noopener"
                                class="flex h-8 w-8 items-center justify-center rounded-full bg-facebook text-white"
                            >
                                <IconBrandFacebook :size="20" />
                            </a>

                            <a
                                href="https://fr.linkedin.com/company/camaqs"
                                target="_blank"
                                rel="noopener"
                                class="flex h-8 w-8 items-center justify-center rounded-full bg-gray-700 text-white"
                            >
                                <IconBrandLinkedin :size="20" />
                            </a>

                            <a
                                href="https://www.youtube.com/channel/UCK8z-A-C9Bd55BHusdfXuwg"
                                target="_blank"
                                rel="noopener"
                                class="flex h-8 w-8 items-center justify-center rounded-full bg-youtube text-white"
                            >
                                <IconBrandYoutube :size="20" />
                            </a>

                            <a
                                href="https://www.instagram.com/camaq/"
                                target="_blank"
                                rel="noopener"
                                class="flex h-8 w-8 items-center justify-center rounded-full bg-gray-700 text-white"
                            >
                                <IconBrandInstagram :size="20" />
                            </a>

                            <a
                                href="https://www.tiktok.com/@camaq"
                                target="_blank"
                                rel="noopener"
                                class="flex h-8 w-8 items-center justify-center rounded-full bg-black text-white"
                            >
                                <IconBrandTiktok :size="20" />
                            </a>
                        </div>
                    </div>
                </div>
                <div class="bg-white px-4 py-16 sm:px-6 lg:col-span-3 lg:px-8 lg:py-24 xl:pl-12">
                    <div class="mx-auto max-w-lg lg:max-w-none">
                        <form
                            class="grid grid-cols-1 gap-y-6"
                            @change="form.errors.clear($event.target.name)"
                            @keyup="form.errors.clear($event.target.name)"
                            @submit.prevent="submit()"
                        >
                            <div>
                                <label for="name" class="label">{{ $t('Full name') }}</label>
                                <input type="text" name="name" id="name" v-model="form.name" class="input" />
                                <p class="form-error" v-if="form.errors.has('name')" v-text="form.errors.get('name')"></p>
                            </div>

                            <div>
                                <label for="email" class="label">{{ $t('Email address') }}</label>
                                <input type="email" name="email" id="email" v-model="form.email" class="input" />
                                <p class="form-error" v-if="form.errors.has('email')" v-text="form.errors.get('email')"></p>
                            </div>

                            <div>
                                <label for="phone_number" class="label">{{ $t('Phone number') }}</label>
                                <input type="tel" name="phone_number" id="phone_number" v-model="form.phone_number" class="input" />
                                <p class="form-error" v-if="form.errors.has('phone_number')" v-text="form.errors.get('phone_number')"></p>
                            </div>

                            <div>
                                <label for="message" class="label">{{ $t('Message') }}</label>
                                <textarea name="message" id="message" v-model="form.message" cols="30" rows="10" class="input"></textarea>
                                <p class="form-error" v-if="form.errors.has('message')" v-text="form.errors.get('message')"></p>
                            </div>

                            <div>
                                <Button :busy="form.processing">
                                    {{ $t('Send the message') }}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>

<script setup>
    import Form from '@/forms';
    import {
        IconBrandFacebook,
        IconBrandInstagram,
        IconBrandLinkedin,
        IconBrandTiktok,
        IconBrandYoutube,
        IconMail,
        IconPhone,
    } from '@tabler/icons-vue';
    import { trans } from 'laravel-vue-i18n';
    import Swal from 'sweetalert2';
    import { reactive } from 'vue';

    const form = reactive(
        new Form({
            name: '',
            email: '',
            phone_number: '',
            message: '',
        })
    );

    function submit() {
        form.post('/contact')
            .then(response => {
                Swal.fire({
                    title: 'Succès',
                    text: trans('Your message has been sent'),
                    icon: 'success',
                    timer: 3000,
                    timerProgressBar: true,
                    showConfirmButton: false,
                });
            })
            .catch(error => {
                if (error.response?.status != 422) {
                    Swal.fire({
                        title: 'Erreur',
                        text: "Une erreur s'est produite lors de l'envoi, veuillez réessayer. Si le problème persiste, veuillez contacter le support à support@camaq.ca",
                        icon: 'error',
                    });
                }
            });
    }
</script>
